/**
 * Framework-agnostic holder for the current bearer token.
 *
 * `ApiProvider` sits above `MiniappSdkProvider` in the tree, so the API layer
 * can't read the SDK directly to refresh an expired token. This module-level
 * store bridges that gap: the API context seeds and reads the token here, while
 * a component mounted under the SDK provider registers the actual refresher
 * (the SDK's `getValidToken`). Because it lives outside React, every caller —
 * REST and `@hqo/service-booking-client` alike — sees the same token.
 *
 * `getToken()` refreshes proactively once the token is within
 * {@link TOKEN_REFRESH_SKEW_MS} of its `exp`, so a long-open webview doesn't
 * keep sending its launch token after it expired.
 */

import { isTokenStale, TOKEN_REFRESH_SKEW_MS } from './jwt-expiry';

export interface RefreshOptions {
  /** Ask the host for a new token even if the current one still looks valid. */
  force: boolean;
}

type Refresher = (options: RefreshOptions) => Promise<string>;
type InFlightRefresh = {
  generation: number;
  promise: Promise<string>;
};

export const STRIP_BEARER = (token: string | undefined | null): string =>
  token?.replace(/bearer /i, '') ?? '';

let currentToken = '';
let refreshGeneration = 0;
let inFlightRefresh: InFlightRefresh | null = null;
let refresher: Refresher | null = null;

const RUN_REFRESH = async (
  generation: number,
  activeRefresher: Refresher,
  options: RefreshOptions,
): Promise<string> => {
  try {
    const nextToken = STRIP_BEARER(await activeRefresher(options));

    if (generation !== refreshGeneration) {
      return currentToken;
    }

    // An empty answer carries nothing usable, so keep what we have.
    if (nextToken) currentToken = nextToken;

    return currentToken;
  } catch {
    // A rejection (the SDK's `TokenRefreshError`: timeout, no host, or a host
    // that can't refresh) keeps the current token. The request that needed it
    // fails and surfaces through the session-expired screen.
    return currentToken;
  } finally {
    if (inFlightRefresh?.generation === generation) inFlightRefresh = null;
  }
};

export const authTokenStore = {
  /** Seeds/overwrites the token (accepts a raw or `Bearer `-prefixed value). */
  setToken(token: string | undefined | null): void {
    currentToken = STRIP_BEARER(token);
  },

  /** Synchronous snapshot of the current token (does not wait for a refresh). */
  peekToken(): string {
    return currentToken;
  },

  /**
   * Returns the latest raw token. If a refresh is in flight it waits for it to
   * settle first, so a request retried right after a 401 deterministically
   * ships the newly minted bearer. Otherwise a token that is about to expire
   * is refreshed first (no-op until a refresher is registered).
   *
   * A request waits for at most one refresh: when it joined one in flight, it
   * takes that outcome rather than asking the host again straight after a
   * failure.
   */
  async getToken(): Promise<string> {
    if (inFlightRefresh) {
      await inFlightRefresh.promise;

      return currentToken;
    }
    if (refresher && isTokenStale(currentToken, TOKEN_REFRESH_SKEW_MS)) {
      return authTokenStore.refresh({ force: false });
    }

    return currentToken;
  },

  /**
   * Refreshes the token via the registered refresher. Concurrent callers share
   * a single in-flight request so a burst of simultaneous 401s triggers only
   * one host round-trip. Resolves to the current token when no refresher is
   * registered (e.g. before the SDK is ready). Never rejects.
   *
   * Forced by default: the reactive callers (a rejected request, the
   * session-expired screen) already know the token is no good.
   */
  refresh(options: RefreshOptions = { force: true }): Promise<string> {
    if (inFlightRefresh) {
      return inFlightRefresh.promise;
    }
    if (!refresher) {
      return Promise.resolve(currentToken);
    }

    const GENERATION = refreshGeneration;
    const ACTIVE_REFRESHER = refresher;
    let resolvePromise: (value: string | PromiseLike<string>) => void = () => undefined;
    const PROMISE = new Promise<string>((resolve) => {
      resolvePromise = resolve;
    });
    inFlightRefresh = { generation: GENERATION, promise: PROMISE };
    resolvePromise(RUN_REFRESH(GENERATION, ACTIVE_REFRESHER, options));

    return PROMISE;
  },

  invalidateRefresh(): void {
    refreshGeneration += 1;
    inFlightRefresh = null;
  },

  registerRefresher(fn: Refresher | null): void {
    refresher = fn;
  },

  /** Test-only: clears all state between specs. */
  reset(): void {
    currentToken = '';
    refreshGeneration += 1;
    inFlightRefresh = null;
    refresher = null;
  },
};
